import { ListPlus, Search, UserRound, X } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useSearchEnd, useTitleSearch } from "../../data/catalog";
import { useListSearch } from "../../data/lists";
import { usePeopleSearch } from "../../data/social";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useVisualViewport } from "../../hooks/useVisualViewport";
import { creatorNames } from "../../lib/credits";
import { CoverStack } from "../lists/ListCard";
import Avatar from "../social/Avatar";
import SearchInput from "../ui/SearchInput";
import WorkThumb from "../works/WorkThumb";

// How many of each Browse looks through the palette shows; the rest are a
// "See all" away, on Browse itself.
const TITLES = 5;
const OTHERS = 3;

type Item = {
  key: string;
  icon: React.ReactNode;
  title: string;
  sub?: string;
  go: () => void;
};

type Section = { label: string; items: Item[] };

const icon = (Icon: typeof Search) => (
  <span className="palette-icon" aria-hidden="true">
    <Icon size="1em" aria-hidden="true" />
  </span>
);

/**
 * Browse from anywhere: ⌘K (Ctrl+K) opens it over the page you're on
 * (`Layout`). It searches what Browse's tabs do, titles, people and public
 * lists, a few of each, and leads to Browse for the rest. The arrow keys
 * move through the results and Enter opens one; a title opens in its
 * modal, over the page under the palette.
 */
const BrowsePalette: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useScrollLock();
  const visible = useVisualViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const q = query.trim();

  // Signed in, as Browse is, for the types you track (ADR 0044).
  const titles = useTitleSearch(q, TITLES);
  const people = usePeopleSearch(q, OTHERS);
  const lists = useListSearch(q, OTHERS);

  // How a title search ended (plan 014's 6d), the words never sent: a
  // title picked, by its rank; a person, a list or Browse took it on.
  const ended = useSearchEnd("palette", q, titles.data?.length);

  const go =
    (to: string, modal = false, rank?: number) =>
    () => {
      if (rank === undefined) ended.handedOff();
      else ended.picked(rank);
      onClose();
      navigate(to, modal ? { state: { background: location } } : undefined);
    };

  const sections: Section[] = q
    ? [
        {
          label: "Titles",
          items: (titles.data ?? []).map((work, i) => ({
            key: `work-${work.id}`,
            icon: <WorkThumb work={work} className="sheet-row-icon" />,
            title: work.title,
            sub: creatorNames(work),
            go: go(`/works/${work.id}`, true, i + 1),
          })),
        },
        {
          label: "People",
          items: (people.data ?? []).map((person) => ({
            key: `person-${person.username}`,
            icon: <Avatar user={person} size="sm" />,
            title: `@${person.username}`,
            go: go(`/u/${encodeURIComponent(person.username)}`),
          })),
        },
        {
          label: "Lists",
          items: (lists.data ?? []).map((list) => ({
            key: `list-${list.id}`,
            icon: (
              <CoverStack works={list.preview} className="sheet-row-icon" />
            ),
            title: list.name,
            sub: list.owner && `by @${list.owner.username}`,
            go: go(`/lists/${list.id}`),
          })),
        },
        {
          label: "Browse",
          items: [
            {
              key: "all",
              icon: icon(Search),
              title: `See all results for “${q}”`,
              go: go(`/browse?${new URLSearchParams({ q })}`),
            },
          ],
        },
      ].filter((section) => section.items.length > 0)
    : [
        {
          label: "Browse",
          items: [
            {
              key: "titles",
              icon: icon(Search),
              title: "Titles",
              path: "/browse",
            },
            {
              key: "people",
              icon: icon(UserRound),
              title: "People",
              path: "/browse/people",
            },
            {
              key: "lists",
              icon: icon(ListPlus),
              title: "Lists",
              path: "/browse/lists",
            },
          ].map(({ path, ...item }) => ({ ...item, go: go(path) })),
        },
      ];
  const items = sections.flatMap((section) => section.items);
  // The highlighted row (the input keeps the focus, so it's
  // `aria-activedescendant`'s), held to the rows there are as results come in.
  const current = Math.min(active, items.length - 1);

  // A new search starts from its first result.
  useEffect(() => setActive(0), [q]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((current + step + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[current].go();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    // The scrim is a pointer's way out; Escape and Close are the keyboard's.
    <div
      className="sheet-backdrop sheet-dialog palette"
      style={visible}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <dialog open className="sheet" aria-label="Browse" onKeyDown={onKeyDown}>
        <div className="sheet-head">
          <h3>Browse</h3>
          <button
            className="btn btn-ghost btn-sm icon-only"
            aria-label="Close"
            onClick={onClose}
          >
            <X size="1em" aria-hidden="true" />
          </button>
        </div>

        <SearchInput
          placeholder="Search titles, people and lists…"
          autoFocus
          value={query}
          onChange={setQuery}
          inputProps={{
            role: "combobox",
            "aria-label": "Search titles, people and lists",
            "aria-expanded": true,
            "aria-controls": "palette-results",
            "aria-activedescendant": `palette-${items[current].key}`,
          }}
        />

        {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a combobox's list: the field keeps the focus and names its option (aria-activedescendant), and a <select> can't hold covers */}
        <div id="palette-results" role="listbox" aria-label="Results">
          {sections.map((section) => (
            <div
              key={section.label}
              className="sheet-section"
              // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the listbox's sections, which an <optgroup> can only hold in a <select>
              role="group"
              aria-label={section.label}
            >
              <div className="sheet-section-label muted" aria-hidden="true">
                {section.label}
              </div>
              {section.items.map((item) => {
                const selected = item === items[current];
                return (
                  // oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- the keyboard picks it from the field (arrows, Enter)
                  <div
                    key={item.key}
                    id={`palette-${item.key}`}
                    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- an <option> can't hold a cover
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    className={selected ? "sheet-row is-active" : "sheet-row"}
                    onClick={item.go}
                    onMouseMove={() => setActive(items.indexOf(item))}
                  >
                    {item.icon}
                    <span className="sheet-row-main">
                      <span className="sheet-row-title">{item.title}</span>
                      {item.sub && (
                        <span className="sheet-row-sub muted">{item.sub}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </dialog>
    </div>
  );
};

export default BrowsePalette;
